import { useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../../config/api";
import { exportStyledExcel, r2, MONEY_FMT, QTY_FMT, TOTAL_FILL } from "../../utils/exportExcel";

export default function CurrentStock() {
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterStatus, setFilterStatus] = useState("all"); // all | low | out
  const printRef = useRef();

  const { data: products = [], isLoading: loadingProducts } = useQuery({
    queryKey: ["products-all"],
    queryFn: () => api.get("/api/products", { params: { all: true } }).then(r =>
      Array.isArray(r.data) ? r.data : (r.data?.data || [])
    ),
  });

  const { data: variants = [], isLoading: loadingVariants } = useQuery({
    queryKey: ["variants-all"],
    // all=true: without it the server returns only 200 variants
    queryFn: () => api.get("/api/variants", { params: { all: true } }).then(r => Array.isArray(r.data) ? r.data : (r.data?.data || [])).catch(() => []),
  });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: () => api.get("/api/settings").then(r => r.data) });
  const rate = Number(settings?.exchange_rate) || 89500;
  const lowDefault = Number(settings?.inventory?.low_stock_default) || 5;
  // cost in USD (a variant without its own cost uses its item's cost)
  const costUsd = (e, parent) => {
    const c = Number(e?.cost) || 0;
    if (c > 0) return (e.cost_currency || "USD") === "USD" ? c : c / rate;
    return parent ? costUsd(parent, null) : 0;
  };
  // an item is LOW when it is at or under ITS minimum (or the shop's default)
  const minOf = (r) => (Number(r.min_stock) > 0 ? Number(r.min_stock) : lowDefault);

  const { data: categories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: () => api.get("/api/categories").then(r => r.data),
  });

  // Build flat rows: one row per variant (variable products) or per product (simple)
  const rows = [];
  products.forEach(p => {
    // only explicit false = service item (older products have no manage_stock field — backend treats them as stocked)
    if (p.manage_stock === false) return;
    const cat = categories.find(c => c._id === p.category_id);
    if (p.type === "variable") {
      const pvariants = variants.filter(v => v.product_id === p._id);
      pvariants.forEach(v => {
        rows.push({
          product_id: p._id,
          product_name: p.name,
          variant_id: v._id,
          variant_name: v.name,
          sku: v.sku || p.sku || "—",
          barcode: String(v.barcode || p.barcode || ""),
          category: cat?.name || "—",
          stock: v.stock ?? 0,
          price_usd: v.price_usd ?? p.price_usd,
          cost_usd: costUsd(v, p),
          min_stock: v.min_stock ?? p.min_stock,
        });
      });
    } else {
      rows.push({
        product_id: p._id,
        product_name: p.name,
        variant_id: null,
        variant_name: null,
        sku: p.sku || "—",
        barcode: String(p.barcode || ""),
        category: cat?.name || "—",
        stock: p.stock ?? 0,
        price_usd: p.price_usd,
        cost_usd: costUsd(p, null),
        min_stock: p.min_stock,
      });
    }
  });

  // Filter
  const filtered = rows.filter(r => {
    const q = search.toLowerCase();
    const matchSearch = !search ||
      r.product_name.toLowerCase().includes(q) ||
      (r.variant_name || "").toLowerCase().includes(q) ||
      r.sku.toLowerCase().includes(q) ||
      r.barcode.toLowerCase().includes(q);
    const matchCat = !filterCategory || r.category === filterCategory;
    const matchStatus =
      filterStatus === "all" ? true :
      filterStatus === "out" ? r.stock <= 0 :
      filterStatus === "low" ? r.stock > 0 && r.stock <= minOf(r) : true;
    return matchSearch && matchCat && matchStatus;
  });

  const totalItems = filtered.length;
  const outOfStock = filtered.filter(r => r.stock <= 0).length;
  const lowStock   = filtered.filter(r => r.stock > 0 && r.stock <= minOf(r)).length;
  const valueCost  = filtered.reduce((s, r) => s + (r.stock > 0 ? r.stock * (r.cost_usd || 0) : 0), 0);
  const valuePrice = filtered.reduce((s, r) => s + (r.stock > 0 && r.price_usd ? r.stock * r.price_usd : 0), 0);

  const stockColor = (qty, r) => {
    if (qty <= 0) return "text-red-600 font-bold";
    if (qty <= minOf(r)) return "text-orange-500 font-semibold";
    return "text-green-600 font-semibold";
  };

  const stockBadge = (qty, r) => {
    if (qty <= 0) return <span className="ml-2 px-1.5 py-0.5 rounded text-xs bg-red-100 text-red-600">Out</span>;
    if (qty <= minOf(r)) return <span className="ml-2 px-1.5 py-0.5 rounded text-xs bg-orange-100 text-orange-600">Low</span>;
    return null;
  };

  // Export to Excel (styled)
  const handleExportExcel = () => {

    exportStyledExcel({
      filename: `current_stock_${new Date().toISOString().slice(0, 10)}`,
      sheetName: "Current Stock",
      title: "Current Stock",
      subtitle: `As of ${new Date().toLocaleDateString("en-GB")} · ${filtered.length} items`,
      columns: [
        { header: "Product", key: "product", width: 26 },
        { header: "Variant", key: "variant", width: 14 },
        { header: "SKU", key: "sku", width: 14 },
        { header: "Category", key: "category", width: 16 },
        { header: "Stock", key: "stock", width: 10, numFmt: QTY_FMT, align: "right" },
        { header: "Cost (USD)", key: "cost", width: 12, numFmt: MONEY_FMT, align: "right" },
        { header: "Price (USD)", key: "price", width: 12, numFmt: MONEY_FMT, align: "right" },
        { header: "Value at cost", key: "vcost", width: 14, numFmt: MONEY_FMT, align: "right" },
        { header: "Value at price", key: "value", width: 14, numFmt: MONEY_FMT, align: "right" },
      ],
      rows: [
        ...filtered.map(r => ({
          product: r.product_name,
          variant: r.variant_name || "",
          sku: r.sku === "—" ? "" : r.sku,
          category: r.category === "—" ? "" : r.category,
          stock: Number(r.stock) || 0,
          cost: r.cost_usd ? r2(r.cost_usd) : "",
          price: r.price_usd ? r2(r.price_usd) : "",
          vcost: r.cost_usd && r.stock > 0 ? r2(r.stock * r.cost_usd) : "",
          value: r.price_usd && r.stock > 0 ? r2(r.stock * r.price_usd) : "",
        })),
        { product: "TOTAL", category: `${filtered.length} items`, vcost: r2(valueCost), value: r2(valuePrice), __bold: true, __fill: TOTAL_FILL },
      ],
    });
  };

  // Print
  const handlePrint = () => {
    const printContent = printRef.current.innerHTML;
    const win = window.open("", "_blank");
    win.document.write(`
      <html><head><title>Current Stock</title>
      <style>
        body { font-family: sans-serif; font-size: 12px; padding: 20px; }
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid #ddd; padding: 6px 10px; text-align: left; }
        th { background: #f3f4f6; font-weight: 600; }
        .out { color: #dc2626; font-weight: bold; }
        .low { color: #ea580c; font-weight: 600; }
        .ok  { color: #16a34a; }
        h2 { margin-bottom: 12px; }
      </style>
      </head><body>
      <h2>Current Stock — ${new Date().toLocaleDateString()}</h2>
      ${printContent}
      </body></html>
    `);
    win.document.close();
    win.print();
  };

  const uniqueCategories = [...new Set(rows.map(r => r.category).filter(c => c !== "—"))];
  const isLoading = loadingProducts || loadingVariants;

  return (
    <div className="p-6 text-gray-800">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Current Stock</h1>
          <p className="text-gray-500 text-sm mt-0.5">Live inventory levels across all products</p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleExportExcel}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export Excel
          </button>
          <button onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-800 text-white text-sm font-medium rounded-lg transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Print
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4 mb-5">
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <p className="text-xs text-gray-500 mb-1">Total SKUs</p>
          <p className="text-2xl font-bold text-gray-800">{totalItems}</p>
        </div>
        <div className="bg-white rounded-xl border border-orange-200 p-4 shadow-sm">
          <p className="text-xs text-orange-500 mb-1">Low Stock (at or under minimum)</p>
          <p className="text-2xl font-bold text-orange-500">{lowStock}</p>
        </div>
        <div className="bg-white rounded-xl border border-red-200 p-4 shadow-sm">
          <p className="text-xs text-red-500 mb-1">Out of Stock</p>
          <p className="text-2xl font-bold text-red-600">{outOfStock}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 flex flex-wrap gap-3">
        <input type="text" placeholder="Search product, variant, SKU, barcode..."
          value={search} onChange={e => setSearch(e.target.value)}
          className="flex-1 min-w-[200px] border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500" />
        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none">
          <option value="">All Categories</option>
          {uniqueCategories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none">
          <option value="all">All Stock</option>
          <option value="low">Low Stock</option>
          <option value="out">Out of Stock</option>
        </select>
        {(search || filterCategory || filterStatus !== "all") && (
          <button onClick={() => { setSearch(""); setFilterCategory(""); setFilterStatus("all"); }}
            className="text-sm text-blue-500 hover:text-blue-700 px-2">
            Clear
          </button>
        )}
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div ref={printRef} className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wider text-gray-500">
                  <th className="text-left px-4 py-3 font-semibold">Product</th>
                  <th className="text-left px-4 py-3 font-semibold">Variant</th>
                  <th className="text-left px-4 py-3 font-semibold">SKU</th>
                  <th className="text-left px-4 py-3 font-semibold">Category</th>
                  <th className="text-right px-4 py-3 font-semibold">Cost</th>
                  <th className="text-right px-4 py-3 font-semibold">Price</th>
                  <th className="text-right px-4 py-3 font-semibold">Value at cost</th>
                  <th className="text-right px-4 py-3 font-semibold">Value at price</th>
                  <th className="text-right px-4 py-3 font-semibold">Stock</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.length === 0 ? (
                  <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-400">No products found.</td></tr>
                ) : filtered.map((r, idx) => (
                  <tr key={r.variant_id || r.product_id} className={`hover:bg-gray-50/50 transition-colors ${r.stock <= 0 ? 'bg-red-50/30' : r.stock <= minOf(r) ? 'bg-orange-50/30' : ''}`}>
                    <td className="px-4 py-2.5 font-medium text-gray-800">{r.product_name}</td>
                    <td className="px-4 py-2.5 text-gray-500">{r.variant_name || <span className="text-gray-300">—</span>}</td>
                    <td className="px-4 py-2.5 text-gray-400 font-mono text-xs">{r.sku}</td>
                    <td className="px-4 py-2.5 text-gray-500">{r.category}</td>

                    <td className="px-4 py-2.5 text-right text-gray-500">{r.cost_usd ? `$${r2(r.cost_usd)}` : <span className="text-amber-600">no cost</span>}</td>
                    <td className="px-4 py-2.5 text-right text-gray-700">{r.price_usd ? `$${r.price_usd}` : "—"}</td>
                    <td className="px-4 py-2.5 text-right text-gray-800 font-medium">{r.cost_usd && r.stock > 0 ? `$${(r.stock * r.cost_usd).toFixed(2)}` : "—"}</td>
                    <td className="px-4 py-2.5 text-right text-gray-500">{r.price_usd && r.stock > 0 ? `$${(r.stock * r.price_usd).toFixed(2)}` : "—"}</td>
                    <td className="px-4 py-2.5 text-right">
                      <span className={stockColor(r.stock, r)}>{r.stock}</span>
                      {stockBadge(r.stock, r)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="border-t border-gray-200 px-4 py-3 bg-gray-50 flex items-center justify-between text-sm">
           <span className="text-xs text-gray-400">Showing {filtered.length} of {rows.length} items</span>
           <span className="font-bold text-gray-800">
             Stock value at cost: ${valueCost.toFixed(2)}
             <span className="font-normal text-gray-500 ml-3">at selling price: ${valuePrice.toFixed(2)} · possible profit ${(valuePrice - valueCost).toFixed(2)}</span>
           </span>
          </div>
        </div>
      )}
    </div>
  );
}